import React from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import MaterialTable from 'material-table';
import Paper from '@material-ui/core/Paper';
import TablePagination from '@material-ui/core/TablePagination';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import NotificationsIcon from '@material-ui/icons/Notifications';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import { makeStyles } from '@material-ui/core/styles';
import { userActions } from 'store/modules/user';
import tableIcons from 'common/tableIcons';
import TablePaginationActions from 'components/shared/TablePaginationActions';
import TableLoadingIndicator from 'components/shared/TableLoadingIndicator';
import routes from 'common/routes';
import UserTableDetail from './TableDetail';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(9),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(8),
    },
    '& .MuiTableFooter-root .MuiTablePagination-root': {
      display: 'block',
      boxSizing: 'border-box',
      clear: 'both',
    },
  },
  paginationToolbar: {
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
      paddingRight: theme.spacing(1),
      paddingLeft: theme.spacing(1),
    },
  },
  paginationSpacer: {
    [theme.breakpoints.down('sm')]: {
      flex: '0 1 auto',
    },
  },
}));

const UsersTable = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const classes = useStyles();

  const {
    users,
    pagination,
    paginatorInfo,
    loading,
  } = useSelector((state) => ({
    users: state.user.users,
    pagination: state.user.pagination,
    paginatorInfo: state.user.paginatorInfo,
    loading: state.user.loading.list,
  }));

  const data = users.map((user) => ({
    ...user,
    settings: JSON.parse(user.settings) || {},
  }));

  const getDisplayedRows = () => {
    const firstItem = pagination.page === 1
      ? 1
      : (pagination.page - 1) * pagination.first;

    const lastItem = pagination.page === paginatorInfo.lastPage
      ? paginatorInfo.total
      : pagination.page * pagination.first;

    return `${firstItem}-${lastItem} de ${paginatorInfo.total}`;
  };

  const goToEdit = (event, rowData) => {
    history.push(routes.user.update.path.replace(':id', rowData.id));
  };

  const onDelete = (event, rowData) => {
    const ids = rowData.map((row) => row.id);
    dispatch(userActions.deleteUsers(ids));
  };

  const onPageChange = (event, newPage) => {
    dispatch(userActions.updatePagination({ page: newPage }));
  };

  return (
    <Paper square className={classes.container}>
      <MaterialTable
        columns={[
          { title: 'Nombre', field: 'first_name' },
          { title: 'Apellido', field: 'last_name' },
          { title: 'Email', field: 'email' },
          { title: 'Nombre de Usuario', field: 'username' },
          { title: 'Tipo', field: 'role' },
        ]}
        data={data}
        icons={tableIcons}
        isLoading={loading}
        detailPanel={[
          (rowData) => ({
            disabled: !(rowData.settings && 'notifications' in rowData.settings),
            icon: () => <NotificationsIcon />,
            openIcon: () => <NotificationsActiveIcon />,
            tooltip: 'Notificaciones',
            render: (row) => {
              const { settings } = row;
              return <UserTableDetail data={settings} />;
            },
          }),
        ]}
        actions={[
          {
            icon: () => <DeleteIcon />,
            position: 'toolbarOnSelect',
            tooltip: 'Eliminar',
            onClick: onDelete,
          },
          {
            icon: () => <EditIcon />,
            position: 'row',
            tooltip: 'Editar',
            onClick: goToEdit,
          },
        ]}
        options={{
          actionsColumnIndex: -1,
          search: false,
          showTitle: false,
          emptyRowsWhenPaging: false,
          draggable: false,
          sorting: false,
          selection: true,
          pageSize: pagination.first,
          initialPage: pagination.page,
          detailPanelType: 'single',
        }}
        localization={{
          header: {
            actions: '',
          },
          body: {
            emptyRowsWhenPaging: 'No hay usuarios que mostrar. Intenta cambiando los filtros',
            emptyDataSourceMessage: 'No hay usuarios que mostrar. Intenta cambiando los filtros',
          },
          toolbar: {
            nRowsSelected: '{0} fila(s) seleccionada(s)',
          },
        }}
        components={{
          OverlayLoading: () => (<TableLoadingIndicator />),
          Pagination: () => (
            <TablePagination
              classes={{
                toolbar: classes.paginationToolbar,
                spacer: classes.paginationSpacer,
              }}
              count={paginatorInfo.total}
              page={pagination.page}
              rowsPerPage={pagination.first}
              rowsPerPageOptions={[pagination.first]}
              labelDisplayedRows={getDisplayedRows}
              onChangePage={onPageChange}
              ActionsComponent={TablePaginationActions}
            />
          ),
        }}
      />
    </Paper>
  );
};

export default UsersTable;
